import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  LinearProgress,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
  Avatar,
  Snackbar,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as DonationIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  AttachMoney,
  People,
  TrendingUp,
  Flag,
  Build as SetupIcon,
  Campaign as CampaignIcon,
  AccountBalanceWallet,
  Star,
  Verified,
  LocalFireDepartment,
  Check,
  Close,
  Visibility,
  Share,
  Comment,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import donationsService, {
  Campaign,
  CampaignInput,
  DonationStats,
  Donation,
  Withdrawal,
  WithdrawalStats,
} from '../../services/donations.service';
import api from '../../services/api';

interface EntityOption {
  id: string;
  desig: string;
  codigo: string;
}


// ============================================
// MAIN COMPONENT
// ============================================

const Donations: React.FC = () => {
  // Tab state
  const [mainTab, setMainTab] = useState(0);

  // Setup state
  const [tablesExist, setTablesExist] = useState<boolean | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  // Global stats
  const [stats, setStats] = useState<DonationStats | null>(null);

  // Campaigns state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(0);
  const [campaignsRowsPerPage, setCampaignsRowsPerPage] = useState(10);
  const [campaignsTotal, setCampaignsTotal] = useState(0);

  // Donations state
  const [donations, setDonations] = useState<Donation[]>([]);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const [donationsPage, setDonationsPage] = useState(0);
  const [donationsRowsPerPage, setDonationsRowsPerPage] = useState(10);

  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [withdrawalsStats, setWithdrawalsStats] = useState<WithdrawalStats | null>(null);
  const [withdrawalsPage, setWithdrawalsPage] = useState(0);
  const [withdrawalsRowsPerPage, setWithdrawalsRowsPerPage] = useState(10);
  const [withdrawalsFilter, setWithdrawalsFilter] = useState<string>('all');

  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const descricaoRef = useRef<string>('');
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);

  // Form state
  const [formData, setFormData] = useState<CampaignInput>({
    titulo: '',
    descricao: '',
    meta_valor: 0,
    data_inicio: '',
    data_fim: '',
    categoria: '',
    tipo: 'UNICA',
    visibilidade: 'PUBLICA',
    estado: 'A',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [error, setError] = useState<string | null>(null);

  // Image/Video/Amounts state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [montantesSugeridos, setMontantesSugeridos] = useState<string>('500,1000,2500,5000');
  // Lei n.º 34/IX/2018
  const [finalidadeFundos, setFinalidadeFundos] = useState<string>('');
  const [valorAtividade, setValorAtividade] = useState<string>('');
  const [naturezaJuridica, setNaturezaJuridica] = useState<string>('');
  const [moradaBeneficiario, setMoradaBeneficiario] = useState<string>('');
  const [plataformaExclusiva, setPlataformaExclusiva] = useState<boolean>(true);

  // Entity state
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entityInputValue, setEntityInputValue] = useState('');
  const entityDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Event state
  interface EventOption { id: number; titulo: string; data_inicio: string; local_nome?: string }
  const [eventOptions, setEventOptions] = useState<EventOption[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [eventInputValue, setEventInputValue] = useState('');
  const eventDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // ============================================
  // CHECK TABLES
  // ============================================

  const checkTables = useCallback(async () => {
    try {
      const status = await donationsService.checkStatus();
      setTablesExist(status.allTablesExist);
      setConnectionError(!!status.connectionError);
      if (!status.allTablesExist && !status.connectionError) {
        setNeedsSetup(true);
      }
      return status.allTablesExist;
    } catch {
      setConnectionError(true);
      setTablesExist(false);
      return false;
    }
  }, []);

  const searchEntities = async (search: string) => {
    if (!search || search.trim().length < 2) {
      setEntityOptions([]);
      setEntityLoading(false);
      return;
    }
    setEntityLoading(true);
    try {
      const response = await api.get(`/admin/entities?search=${encodeURIComponent(search.trim())}&limit=10`);
      setEntityOptions(response.data?.data || []);
    } catch {
      setEntityOptions([]);
    } finally {
      setEntityLoading(false);
    }
  };

  const searchEvents = async (search: string) => {
    if (!search || search.trim().length < 2) { setEventOptions([]); setEventLoading(false); return; }
    setEventLoading(true);
    try {
      const response = await api.get(`/admin/events?search=${encodeURIComponent(search.trim())}&limit=10`);
      const data = response.data?.events || response.data?.data || [];
      setEventOptions(data.map((e: any) => ({ id: e.id || e.ID, titulo: e.titulo || e.TITULO, data_inicio: e.data_inicio || e.DATA_INICIO, local_nome: e.local_nome })));
    } catch { setEventOptions([]); } finally { setEventLoading(false); }
  };

  const handleEventInputChange = (_: React.SyntheticEvent, value: string) => {
    setEventInputValue(value);
    if (eventDebounceRef.current) clearTimeout(eventDebounceRef.current);
    if (!value || value.trim().length < 2) { setEventOptions([]); return; }
    setEventLoading(true);
    eventDebounceRef.current = setTimeout(() => searchEvents(value), 350);
  };

  const handleEntityInputChange = (_: React.SyntheticEvent, value: string) => {
    setEntityInputValue(value);
    if (entityDebounceRef.current) clearTimeout(entityDebounceRef.current);
    if (!value || value.trim().length < 2) { setEntityOptions([]); return; }
    setEntityLoading(true);
    entityDebounceRef.current = setTimeout(() => searchEntities(value), 350);
  };

  const handleSetup = async () => {
    setSetupLoading(true);
    setError(null);
    try {
      await donationsService.setupTables();
      setNeedsSetup(false);
      setTablesExist(true);
      setSnackbar({ open: true, message: 'Tabelas criadas com sucesso!', severity: 'success' });
      fetchCampaigns();
      fetchStats();
    } catch (err: any) {
      setError(err.message || 'Erro ao configurar tabelas');
    } finally {
      setSetupLoading(false);
    }
  };

  // ============================================
  // FETCH DATA
  // ============================================

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await donationsService.getStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const response = await donationsService.getCampaigns({
        page: campaignsPage + 1,
        limit: campaignsRowsPerPage,
      });
      setCampaigns(response.data || []);
      setCampaignsTotal(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      if (err.message?.includes('tabela') || err.message?.includes('Table')) {
        setNeedsSetup(true);
      }
    } finally {
      setCampaignsLoading(false);
    }
  }, [campaignsPage, campaignsRowsPerPage]);

  const fetchDonations = useCallback(async () => {
    setDonationsLoading(true);
    try {
      const response = await donationsService.getDonations({
        page: donationsPage + 1,
        limit: donationsRowsPerPage,
      });
      setDonations(response.data || []);
    } catch (err) {
      console.error('Error fetching donations:', err);
    } finally {
      setDonationsLoading(false);
    }
  }, [donationsPage, donationsRowsPerPage]);

  const fetchWithdrawals = useCallback(async () => {
    setWithdrawalsLoading(true);
    try {
      const response = await donationsService.getWithdrawals({
        estado: withdrawalsFilter === 'all' ? undefined : withdrawalsFilter,
        page: withdrawalsPage + 1,
        limit: withdrawalsRowsPerPage,
      });
      setWithdrawals(response.data || []);
      setWithdrawalsStats(response.stats);
    } catch (err) {
      console.error('Error fetching withdrawals:', err);
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [withdrawalsFilter, withdrawalsPage, withdrawalsRowsPerPage]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    checkTables();
  }, [checkTables]);

  useEffect(() => {
    if (tablesExist === true) {
      fetchStats();
      fetchCampaigns();
    }
  }, [tablesExist, fetchStats, fetchCampaigns]);

  useEffect(() => {
    if (tablesExist === true && mainTab === 1) {
      fetchDonations();
    }
  }, [tablesExist, mainTab, fetchDonations]);

  useEffect(() => {
    if (tablesExist === true && mainTab === 2) {
      fetchWithdrawals();
    }
  }, [tablesExist, mainTab, fetchWithdrawals]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleOpenEdit = async (campaign?: Campaign) => {
    setImageFile(null);
    setSelectedEntity(null);
    setEntityOptions([]);
    setEntityInputValue('');
    setSelectedEvent(null);
    setEventOptions([]);
    setEventInputValue('');
    if (campaign) {
      setSelectedCampaign(campaign);
      descricaoRef.current = campaign.DESCRICAO || '';
      setFormData({
        titulo: campaign.TITULO || '',
        descricao: campaign.DESCRICAO || '',
        meta_valor: campaign.META_VALOR || 0,
        data_inicio: campaign.DATA_INICIO?.split('T')[0] || '',
        data_fim: campaign.DATA_FIM?.split('T')[0] || '',
        categoria: campaign.CATEGORIA || '',
        tipo: campaign.TIPO || 'UNICA',
        visibilidade: campaign.VISIBILIDADE || 'PUBLICA',
        estado: campaign.ESTADO || 'A',
        entidade_id: campaign.ENTIDADE_ID || null,
      });
      // Load existing image/video/amounts
      setImagePreview(campaign.IMAGEM || campaign.IMAGEM_CAPA || null);
      setVideoUrl(campaign.VIDEO_URL || '');
      setMontantesSugeridos(campaign.MONTANTES_SUGERIDOS || '500,1000,2500,5000');
      setFinalidadeFundos((campaign as any).FINALIDADE_FUNDOS || '');
      setValorAtividade((campaign as any).VALOR_ATIVIDADE != null ? String((campaign as any).VALOR_ATIVIDADE) : '');
      setNaturezaJuridica((campaign as any).NATUREZA_JURIDICA || '');
      setMoradaBeneficiario((campaign as any).MORADA_BENEFICIARIO || '');
      setPlataformaExclusiva((campaign as any).PLATAFORMA_EXCLUSIVA !== false);
      // Pre-load entity if set
      if (campaign.ENTIDADE_ID) {
        try {
          const response = await api.get(`/admin/entities/${campaign.ENTIDADE_ID}`);
          const e = response.data?.data;
          if (e) {
            const opt = { id: e.id, desig: e.desig, codigo: e.codigo };
            setSelectedEntity(opt);
            setEntityInputValue(`${e.desig} (${e.codigo})`);
            setEntityOptions([opt]);
          }
        } catch { /* ignore */ }
      }
      // Pre-load linked event
      const eventoId = (campaign as any).EVENTO_ID;
      if (eventoId) {
        try {
          const response = await api.get(`/admin/events/${eventoId}`);
          const ev = response.data?.data || response.data;
          if (ev) {
            const opt = { id: ev.id, titulo: ev.titulo, data_inicio: ev.data_inicio, local_nome: ev.local_nome };
            setSelectedEvent(opt);
            setEventInputValue(ev.titulo);
            setEventOptions([opt]);
          }
        } catch { /* ignore */ }
      }
    } else {
      setSelectedCampaign(null);
      descricaoRef.current = '';
      setFormData({
        titulo: '',
        descricao: '',
        meta_valor: 0,
        data_inicio: '',
        data_fim: '',
        categoria: '',
        tipo: 'UNICA',
        visibilidade: 'PUBLICA',
        estado: 'A',
        entidade_id: null,
      });
      setImagePreview(null);
      setVideoUrl('');
      setMontantesSugeridos('500,1000,2500,5000');
      setFinalidadeFundos('');
      setValorAtividade('');
      setNaturezaJuridica('');
      setMoradaBeneficiario('');
      setPlataformaExclusiva(true);
    }
    setEditDialog(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSave = async () => {
    try {
      // Build form data with image/video
      const dataToSend: any = {
        ...formData,
        video_url: videoUrl || null,
        entidade_id: selectedEntity?.id ?? null,
        montantes_sugeridos: montantesSugeridos || null,
        evento_id: selectedEvent?.id ?? null,
        // Lei n.º 34/IX/2018
        finalidade_fundos: finalidadeFundos || null,
        valor_atividade: valorAtividade ? Number(valorAtividade) : null,
        natureza_juridica: naturezaJuridica || null,
        morada_beneficiario: moradaBeneficiario || null,
        plataforma_exclusiva: plataformaExclusiva,
      };

      // If there's a new image file, convert to base64
      if (imageFile) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data:image/xxx;base64, prefix
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.readAsDataURL(imageFile);
        });
        dataToSend.imagem = base64;
        dataToSend.imagem_mime_type = imageFile.type;
      }

      if (selectedCampaign) {
        await donationsService.updateCampaign(selectedCampaign.ID, dataToSend);
        setSnackbar({ open: true, message: 'Campanha actualizada!', severity: 'success' });
      } else {
        await donationsService.createCampaign(dataToSend);
        setSnackbar({ open: true, message: 'Campanha criada!', severity: 'success' });
      }
      setEditDialog(false);
      setImageFile(null);
      setImagePreview(null);
      setVideoUrl('');
      fetchCampaigns();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleDelete = async (campaign: Campaign) => {
    if (!window.confirm(`Eliminar campanha "${campaign.TITULO}"?`)) return;
    try {
      await donationsService.deleteCampaign(campaign.ID);
      setSnackbar({ open: true, message: 'Campanha desactivada!', severity: 'success' });
      fetchCampaigns();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro ao eliminar', severity: 'error' });
    }
  };

  const handleFeature = async (campaign: Campaign, field: 'destaque' | 'trending' | 'verificada') => {
    try {
      await donationsService.featureCampaign(campaign.ID, {
        [field]: !campaign[field.toUpperCase() as keyof Campaign],
      });
      fetchCampaigns();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro ao actualizar', severity: 'error' });
    }
  };

  const handleConfirmDonation = async (donation: Donation) => {
    try {
      await donationsService.confirmDonation(donation.ID);
      setSnackbar({ open: true, message: 'Doação confirmada!', severity: 'success' });
      fetchDonations();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro', severity: 'error' });
    }
  };

  const handleCancelDonation = async (donation: Donation) => {
    const msg = donation.ESTADO === 'CONFIRMADO'
      ? `Reverter doação CONFIRMADA de ${formatCurrency(donation.VALOR)}? Isto vai subtrair o valor da campanha.`
      : 'Cancelar esta doação?';
    if (!window.confirm(msg)) return;
    try {
      await donationsService.cancelDonation(donation.ID);
      setSnackbar({ open: true, message: 'Doação cancelada!', severity: 'success' });
      fetchDonations();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro', severity: 'error' });
    }
  };

  const handleApproveWithdrawal = async (withdrawal: Withdrawal) => {
    try {
      await donationsService.approveWithdrawal(withdrawal.ID);
      setSnackbar({ open: true, message: 'Saque aprovado!', severity: 'success' });
      fetchWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro', severity: 'error' });
    }
  };

  const handleRejectWithdrawal = async (withdrawal: Withdrawal) => {
    const motivo = window.prompt('Motivo da rejeição:');
    if (!motivo) return;
    try {
      await donationsService.rejectWithdrawal(withdrawal.ID, motivo);
      setSnackbar({ open: true, message: 'Saque rejeitado!', severity: 'success' });
      fetchWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro', severity: 'error' });
    }
  };

  const handleCompleteWithdrawal = async (withdrawal: Withdrawal) => {
    const referencia = window.prompt('Referência da transferência:');
    if (!referencia) return;
    try {
      await donationsService.completeWithdrawal(withdrawal.ID, { referencia_transferencia: referencia });
      setSnackbar({ open: true, message: 'Saque concluído!', severity: 'success' });
      fetchWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Erro', severity: 'error' });
    }
  };

  // ============================================
  // HELPERS
  // ============================================

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const getProgressPercentage = (arrecadado: number, meta: number) => {
    if (meta <= 0) return 0;
    return Math.min((arrecadado / meta) * 100, 100);
  };

  const getStatusChip = (estado?: string) => {
    switch (estado) {
      case 'A': return <Chip size="small" color="success" label="Activa" icon={<ActiveIcon />} />;
      case 'P': return <Chip size="small" color="warning" label="Pendente" />;
      case 'F': return <Chip size="small" color="info" label="Finalizada" />;
      case 'S': return <Chip size="small" color="error" label="Suspensa" />;
      case 'C': return <Chip size="small" color="default" label="Cancelada" />;
      default: return <Chip size="small" color="default" label="Inactiva" icon={<InactiveIcon />} />;
    }
  };

  const getDonationStatusChip = (estado?: string) => {
    switch (estado) {
      case 'CONFIRMADO': return <Chip size="small" color="success" label="Confirmado" />;
      case 'PENDENTE': return <Chip size="small" color="warning" label="Pendente" />;
      case 'CANCELADO': return <Chip size="small" color="error" label="Cancelado" />;
      case 'REEMBOLSADO': return <Chip size="small" color="info" label="Reembolsado" />;
      default: return <Chip size="small" label={estado || '-'} />;
    }
  };

  const getWithdrawalStatusChip = (estado?: string) => {
    switch (estado) {
      case 'PENDENTE': return <Chip size="small" color="warning" label="Pendente" />;
      case 'APROVADO': return <Chip size="small" color="info" label="Aprovado" />;
      case 'PROCESSANDO': return <Chip size="small" color="primary" label="Processando" />;
      case 'CONCLUIDO': return <Chip size="small" color="success" label="Concluído" />;
      case 'REJEITADO': return <Chip size="small" color="error" label="Rejeitado" />;
      default: return <Chip size="small" label={estado || '-'} />;
    }
  };

  // ============================================
  // RENDER - Loading/Error states
  // ============================================

  if (tablesExist === null) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>Gestão de Doações</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      </Box>
    );
  }

  if (connectionError) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>Gestão de Doações</Typography>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => checkTables()}>Tentar novamente</Button>
        }>
          Não foi possível conectar à base de dados.
        </Alert>
      </Box>
    );
  }

  // ============================================
  // RENDER - Main
  // ============================================

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {needsSetup && (
        <Alert severity="warning" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" startIcon={setupLoading ? <CircularProgress size={16} /> : <SetupIcon />}
            onClick={handleSetup} disabled={setupLoading}>
            {setupLoading ? 'A configurar...' : 'Configurar Tabelas'}
          </Button>
        }>
          As tabelas de doações GoFundMe ainda não foram configuradas.
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>Gestão de Doações</Typography>
        {mainTab === 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" color="warning" size="small" startIcon={<SetupIcon />}
              onClick={async () => {
                try {
                  const result = await donationsService.runMigrate();
                  const erros = result.migrations.filter(m => m.status === 'error');
                  if (erros.length > 0) {
                    setSnackbar({ open: true, severity: 'error', message: `Erro: ${erros.map(e => `${e.column}: ${e.error}`).join('; ')}` });
                  } else {
                    const added = result.migrations.filter(m => m.status === 'added').map(m => m.column);
                    setSnackbar({ open: true, severity: 'success', message: added.length ? `Colunas adicionadas: ${added.join(', ')}` : 'BD já actualizada' });
                  }
                } catch (err: any) {
                  setSnackbar({ open: true, severity: 'error', message: err.message || 'Erro na migração' });
                }
              }}>
              Migrar BD
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { fetchCampaigns(); fetchStats(); }}>
              Atualizar
            </Button>
            <Button variant="outlined" color="secondary" startIcon={<CampaignIcon />}
              onClick={async () => {
                try {
                  setError(null);
                  const result = await donationsService.seedDemo();
                  if (result.success) {
                    fetchCampaigns();
                    fetchStats();
                    alert(result.message);
                  }
                } catch (err: any) {
                  setError(err?.response?.data?.error || 'Erro ao criar campanhas demo');
                }
              }}>
              Campanhas Demo
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
              Nova Campanha
            </Button>
          </Box>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon color="primary" />
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats?.campanhas_ativas || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Campanhas Activas</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag color="info" />
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats?.campanhas_finalizadas || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Finalizadas</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DonationIcon color="error" />
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats?.total_doacoes || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Doações</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="success" />
              <Box>
                <Typography variant="h6" fontWeight={700}>{formatCurrency(stats?.valor_total_arrecadado)}</Typography>
                <Typography variant="caption" color="text.secondary">Total Arrecadado</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People color="secondary" />
              <Box>
                <Typography variant="h5" fontWeight={700}>{stats?.total_doadores || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Doadores</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp color="warning" />
              <Box>
                <Typography variant="h6" fontWeight={700}>{formatCurrency(stats?.media_doacao)}</Typography>
                <Typography variant="caption" color="text.secondary">Média/Doação</Typography>
              </Box>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} variant="fullWidth">
          <Tab icon={<CampaignIcon />} label="Campanhas" iconPosition="start" />
          <Tab icon={<DonationIcon />} label="Doações" iconPosition="start" />
          <Tab icon={<AccountBalanceWallet />} label="Saques" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Campaigns */}
      {mainTab === 0 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campanha</TableCell>
                  <TableCell align="center">Badges</TableCell>
                  <TableCell>Progresso</TableCell>
                  <TableCell align="center">Stats</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignsLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : campaigns.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhuma campanha encontrada</Typography>
                  </TableCell></TableRow>
                ) : campaigns.map((campaign) => {
                  const progress = getProgressPercentage(Number(campaign.VALOR_ARRECADADO) || 0, Number(campaign.META_VALOR) || 0);
                  return (
                    <TableRow key={campaign.ID} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar src={campaign.IMAGEM} sx={{ bgcolor: 'primary.main' }}>
                            <CampaignIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{campaign.TITULO}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {campaign.CATEGORIA || campaign.TIPO}
                            </Typography>
                            {campaign.entidade_desig && (
                              <Typography variant="caption" color="primary.main" display="block">
                                {campaign.entidade_desig}
                              </Typography>
                            )}
                            {(campaign as any).evento_titulo && (
                              <Typography variant="caption" color="secondary.main" display="block">
                                🎟 {(campaign as any).evento_titulo}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Verificada">
                            <IconButton size="small" onClick={() => handleFeature(campaign, 'verificada')}
                              color={campaign.VERIFICADA ? 'success' : 'default'}>
                              <Verified fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Destaque">
                            <IconButton size="small" onClick={() => handleFeature(campaign, 'destaque')}
                              color={campaign.DESTAQUE ? 'warning' : 'default'}>
                              <Star fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Trending">
                            <IconButton size="small" onClick={() => handleFeature(campaign, 'trending')}
                              color={campaign.TRENDING ? 'error' : 'default'}>
                              <LocalFireDepartment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress variant="determinate" value={progress}
                              sx={{ height: 8, borderRadius: 4 }}
                              color={progress >= 100 ? 'success' : 'primary'} />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 40 }}>{(Number(progress) || 0).toFixed(0)}%</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(campaign.VALOR_ARRECADADO)} / {formatCurrency(campaign.META_VALOR)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Doadores">
                            <Chip size="small" icon={<People />} label={campaign.total_doadores || 0} />
                          </Tooltip>
                          <Tooltip title="Views">
                            <Chip size="small" icon={<Visibility />} label={campaign.TOTAL_VIEWS || 0} variant="outlined" />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(campaign.ESTADO)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenEdit(campaign)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(campaign)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={campaignsTotal} page={campaignsPage}
            onPageChange={(_, p) => setCampaignsPage(p)} rowsPerPage={campaignsRowsPerPage}
            onRowsPerPageChange={(e) => { setCampaignsRowsPerPage(parseInt(e.target.value, 10)); setCampaignsPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Linhas:" />
        </Paper>
      )}

      {/* Tab 1: Donations */}
      {mainTab === 1 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDonations}>Atualizar</Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Doador</TableCell>
                  <TableCell>Campanha</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {donationsLoading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : donations.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhuma doação encontrada</Typography>
                  </TableCell></TableRow>
                ) : donations.map((donation) => (
                  <TableRow key={donation.ID} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {donation.ANONIMO ? 'Anónimo' : (donation as any).DOADOR_NOME_REAL || donation.DOADOR_NOME || 'N/A'}
                      </Typography>
                      {donation.DOADOR_EMAIL && (
                        <Typography variant="caption" color="text.secondary">{donation.DOADOR_EMAIL}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{donation.campanha_titulo || '-'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {formatCurrency(donation.VALOR)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {donation.DATA_DONATIVO ? new Date(donation.DATA_DONATIVO).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getDonationStatusChip(donation.ESTADO)}</TableCell>
                    <TableCell align="center">
                      {donation.ESTADO === 'PENDENTE' && (
                        <>
                          <Tooltip title="Confirmar">
                            <IconButton size="small" color="success" onClick={() => handleConfirmDonation(donation)}>
                              <Check fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                            <IconButton size="small" color="error" onClick={() => handleCancelDonation(donation)}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {donation.ESTADO === 'CONFIRMADO' && (
                        <Tooltip title="Reverter (cancelar doação confirmada e subtrair da campanha)">
                          <IconButton size="small" color="warning" onClick={() => handleCancelDonation(donation)}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {['CANCELADO', 'PENDENTE'].includes(donation.ESTADO || '') && (
                        <Tooltip title="Eliminar da BD">
                          <IconButton size="small" color="error" onClick={async () => {
                            if (!window.confirm(`Eliminar permanentemente esta doação de ${formatCurrency(donation.VALOR)}?`)) return;
                            try {
                              await donationsService.deleteDonation(donation.ID);
                              setSnackbar({ open: true, message: 'Doação eliminada', severity: 'success' });
                              fetchDonations();
                              fetchStats();
                            } catch (err: any) {
                              setSnackbar({ open: true, message: err.message || 'Erro ao eliminar', severity: 'error' });
                            }
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={-1} page={donationsPage}
            onPageChange={(_, p) => setDonationsPage(p)} rowsPerPage={donationsRowsPerPage}
            onRowsPerPageChange={(e) => { setDonationsRowsPerPage(parseInt(e.target.value, 10)); setDonationsPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Linhas:" />
        </Paper>
      )}

      {/* Tab 2: Withdrawals */}
      {mainTab === 2 && (
        <>
          {withdrawalsStats && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ bgcolor: 'warning.light' }}><CardContent sx={{ py: 1, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>{withdrawalsStats.pendentes || 0}</Typography>
                  <Typography variant="caption">Pendentes</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ bgcolor: 'info.light' }}><CardContent sx={{ py: 1, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>{withdrawalsStats.aprovados || 0}</Typography>
                  <Typography variant="caption">Aprovados</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ bgcolor: 'success.light' }}><CardContent sx={{ py: 1, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>{withdrawalsStats.concluidos || 0}</Typography>
                  <Typography variant="caption">Concluídos</Typography>
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <Card sx={{ bgcolor: 'error.light' }}><CardContent sx={{ py: 1, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700}>{withdrawalsStats.rejeitados || 0}</Typography>
                  <Typography variant="caption">Rejeitados</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>
          )}
          <Paper>
            <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <TextField select size="small" value={withdrawalsFilter} onChange={(e) => setWithdrawalsFilter(e.target.value)}
                sx={{ minWidth: 150 }}>
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="PENDENTE">Pendentes</MenuItem>
                <MenuItem value="APROVADO">Aprovados</MenuItem>
                <MenuItem value="CONCLUIDO">Concluídos</MenuItem>
                <MenuItem value="REJEITADO">Rejeitados</MenuItem>
              </TextField>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchWithdrawals}>Atualizar</Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campanha</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                    <TableCell align="right">Líquido</TableCell>
                    <TableCell>Banco</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acções</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {withdrawalsLoading ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                  ) : withdrawals.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum saque encontrado</Typography>
                    </TableCell></TableRow>
                  ) : withdrawals.map((withdrawal) => (
                    <TableRow key={withdrawal.ID} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{withdrawal.campanha_titulo || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{withdrawal.ORGANIZADOR_NOME}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(withdrawal.VALOR)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>-{formatCurrency(withdrawal.TAXA_PLATAFORMA)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>{formatCurrency(withdrawal.VALOR_LIQUIDO)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{withdrawal.BANCO_NOME}</Typography><br/>
                        <Typography variant="caption" color="text.secondary">{withdrawal.BANCO_IBAN}</Typography>
                      </TableCell>
                      <TableCell>{getWithdrawalStatusChip(withdrawal.ESTADO)}</TableCell>
                      <TableCell align="center">
                        {withdrawal.ESTADO === 'PENDENTE' && (
                          <>
                            <Tooltip title="Aprovar">
                              <IconButton size="small" color="success" onClick={() => handleApproveWithdrawal(withdrawal)}>
                                <Check fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Rejeitar">
                              <IconButton size="small" color="error" onClick={() => handleRejectWithdrawal(withdrawal)}>
                                <Close fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {withdrawal.ESTADO === 'APROVADO' && (
                          <Tooltip title="Concluir">
                            <IconButton size="small" color="primary" onClick={() => handleCompleteWithdrawal(withdrawal)}>
                              <Check fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={-1} page={withdrawalsPage}
              onPageChange={(_, p) => setWithdrawalsPage(p)} rowsPerPage={withdrawalsRowsPerPage}
              onRowsPerPageChange={(e) => { setWithdrawalsRowsPerPage(parseInt(e.target.value, 10)); setWithdrawalsPage(0); }}
              rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Linhas:" />
          </Paper>
        </>
      )}

      {/* Campaign Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon />
          {selectedCampaign ? 'Editar Campanha' : 'Nova Campanha'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Título da Campanha" value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  Descrição
                </Typography>
                <TextField fullWidth multiline rows={4} label="Descrição"
                  value={formData.descricao || ''}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth type="number" label="Meta (Valor)" value={formData.meta_valor || ''}
                onChange={(e) => setFormData({ ...formData, meta_valor: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">CVE</InputAdornment> }} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth select label="Categoria" value={formData.categoria}
                onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}>
                <MenuItem value="">Seleccionar...</MenuItem>
                <MenuItem value="SAUDE">Saúde & Médico</MenuItem>
                <MenuItem value="EDUCACAO">Educação</MenuItem>
                <MenuItem value="EMERGENCIA">Emergência</MenuItem>
                <MenuItem value="MEMORIAL">Memorial & Funeral</MenuItem>
                <MenuItem value="ANIMAIS">Animais</MenuItem>
                <MenuItem value="COMUNIDADE">Comunidade</MenuItem>
                <MenuItem value="DESPORTO">Desporto</MenuItem>
                <MenuItem value="CRIATIVO">Criativo & Artes</MenuItem>
                <MenuItem value="NEGOCIO">Negócio</MenuItem>
                <MenuItem value="OUTRO">Outro</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="date" label="Data Início" value={formData.data_inicio}
                onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth type="date" label="Data Fim" value={formData.data_fim}
                onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth select label="Tipo" value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}>
                <MenuItem value="UNICA">Única</MenuItem>
                <MenuItem value="RECORRENTE">Recorrente</MenuItem>
                <MenuItem value="EMERGENCIA">Emergência</MenuItem>
                <MenuItem value="MEMORIAL">Memorial</MenuItem>
                <MenuItem value="EQUIPA">Equipa</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth select label="Visibilidade" value={formData.visibilidade}
                onChange={(e) => setFormData({ ...formData, visibilidade: e.target.value })}>
                <MenuItem value="PUBLICA">Pública</MenuItem>
                <MenuItem value="PRIVADA">Privada</MenuItem>
                <MenuItem value="RESTRITA">Restrita</MenuItem>
              </TextField>
            </Grid>
            {selectedCampaign && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth select label="Estado" value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}>
                  <MenuItem value="A">Activa</MenuItem>
                  <MenuItem value="P">Pendente</MenuItem>
                  <MenuItem value="I">Inactiva</MenuItem>
                  <MenuItem value="F">Finalizada</MenuItem>
                  <MenuItem value="S">Suspensa</MenuItem>
                  <MenuItem value="C">Cancelada</MenuItem>
                </TextField>
              </Grid>
            )}

            {/* Montantes sugeridos */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Montantes sugeridos (CVE)"
                value={montantesSugeridos}
                onChange={(e) => setMontantesSugeridos(e.target.value)}
                placeholder="500,1000,2500,5000"
                helperText="Valores separados por vírgula que aparecem como atalhos na página de doação"
              />
            </Grid>

            {/* Lei n.º 34/IX/2018 — Conformidade legal */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                Conformidade Legal — Lei n.º 34/IX/2018
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth multiline rows={3}
                label="Finalidade dos fundos (Art. 14)"
                value={finalidadeFundos}
                onChange={(e) => setFinalidadeFundos(e.target.value)}
                placeholder="Descreva o destino dos fundos angariados..."
                helperText="Obrigatório: descrição clara do destino dos fundos (Art. 14, alínea a)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="number"
                label="Valor real da atividade (CVE)"
                value={valorAtividade}
                onChange={(e) => setValorAtividade(e.target.value)}
                helperText={
                  valorAtividade && formData.meta_valor && Number(formData.meta_valor) > Number(valorAtividade) * 10
                    ? `⚠️ Meta excede o limite legal (máx: ${Number(valorAtividade) * 10} CVE) — Art. 13`
                    : valorAtividade ? `Meta máx. permitida: ${(Number(valorAtividade) * 10).toLocaleString('pt-CV')} CVE — Art. 13` : 'Opcional mas recomendado (Art. 13)'
                }
                FormHelperTextProps={{ error: !!(valorAtividade && formData.meta_valor && Number(formData.meta_valor) > Number(valorAtividade) * 10) }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select fullWidth
                label="Natureza jurídica do beneficiário (Art. 7)"
                value={naturezaJuridica}
                onChange={(e) => setNaturezaJuridica(e.target.value)}
              >
                <MenuItem value="">Não especificado</MenuItem>
                <MenuItem value="SINGULAR">Pessoa Singular</MenuItem>
                <MenuItem value="COLETIVA">Pessoa Coletiva</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Morada do beneficiário (Art. 7)"
                value={moradaBeneficiario}
                onChange={(e) => setMoradaBeneficiario(e.target.value)}
                placeholder="Rua, Cidade, País"
                helperText="Morada/sede do beneficiário (Art. 7)"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={<Switch checked={plataformaExclusiva} onChange={(e) => setPlataformaExclusiva(e.target.checked)} color="primary" />}
                label={
                  <Typography variant="body2">
                    Plataforma exclusiva — confirmo que esta campanha não está disponível noutras plataformas (Art. 13, n.º 2)
                  </Typography>
                }
              />
            </Grid>

            {/* Entity */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={entityOptions}
                value={selectedEntity}
                inputValue={entityInputValue}
                loading={entityLoading}
                filterOptions={(x) => x}
                getOptionLabel={(o) => `${o.desig} (${o.codigo})`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText={
                  entityInputValue.trim().length < 2
                    ? 'Escreva pelo menos 2 caracteres para pesquisar'
                    : 'Nenhuma entidade encontrada'
                }
                onInputChange={handleEntityInputChange}
                onChange={(_, value) => {
                  setSelectedEntity(value);
                  setEntityInputValue(value ? `${value.desig} (${value.codigo})` : '');
                  setFormData((f) => ({ ...f, entidade_id: value?.id ?? null }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entidade (opcional)"
                    placeholder="Escreva para pesquisar..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {entityLoading && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Evento ligado */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={eventOptions}
                value={selectedEvent}
                inputValue={eventInputValue}
                loading={eventLoading}
                filterOptions={(x) => x}
                getOptionLabel={(o) => `${o.titulo}${o.data_inicio ? ` — ${new Date(o.data_inicio).toLocaleDateString('pt-PT', { timeZone: 'UTC' })}` : ''}${o.local_nome ? ` · ${o.local_nome}` : ''}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText={eventInputValue.trim().length < 2 ? 'Escreva 2+ caracteres para pesquisar' : 'Nenhum evento encontrado'}
                onInputChange={handleEventInputChange}
                onChange={(_, value) => {
                  setSelectedEvent(value);
                  setEventInputValue(value ? value.titulo : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Evento ligado (opcional)"
                    placeholder="Escreva para pesquisar evento..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{eventLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />
            </Grid>

            {/* Image Upload Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Imagem da Campanha
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  position: 'relative',
                }}
              >
                {imagePreview ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        borderRadius: 8,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Clique ou arraste uma imagem
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      JPG, PNG, GIF (máx. 5MB)
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </Grid>

            {/* Video URL */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="URL do Vídeo (YouTube, Vimeo)"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                InputProps={{
                  startAdornment: <VideoIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                helperText="Cole o link do YouTube ou Vimeo para adicionar um vídeo à campanha"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.titulo || !formData.meta_valor}>
            {selectedCampaign ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Donations;
